import { ROLE_CLIENT_ADMIN } from '@hixme/role-policy'

// Demo companies are named "ZZ - DEMO - <name>" and flagged as test clients.
// The backend isDemo flag is not reliably surfaced yet, so the name prefix is
// the working signal. Detection is kept self-contained rather than importing
// the DemoCustomer utils: those live at different paths on master
// (DemoCompanyManagement/) vs develop (CustomerManagement/), and this branch
// merges to develop — a cross-branch import would break at merge.
const DEMO_NAME_PREFIX = 'ZZ - DEMO - '

// The only sidebar hrefs a demo company's HR admin may see; their sub-paths
// (e.g. /employees/:id/coverage, /open-enrollment/:section) are allowed too.
const DEMO_ALLOWED_NAV_HREFS = [
  '/',
  '/enrollmentdashboard',
  '/open-enrollment',
  '/employees',
]

export type CompanyLike = {
  name?: string
  companyName?: string
  isDemo?: boolean
}

/** True when the company is a demo company (isDemo flag or the demo name prefix). */
export function isDemoCompany(company?: CompanyLike): boolean {
  if (!company) return false
  if (company.isDemo === true) return true
  const name = company.name ?? company.companyName
  return Boolean(name && name.startsWith(DEMO_NAME_PREFIX))
}

/**
 * True when the current user is a client HR admin whose selected company is a
 * demo company. Platform super-admins and brokers are never restricted.
 */
export function isDemoHrAdminRestricted(
  role?: string,
  company?: CompanyLike
): boolean {
  return role === ROLE_CLIENT_ADMIN && isDemoCompany(company)
}

/** True when a sidebar href is on the demo allow-list (visible to a demo HR admin). */
export function isDemoAllowedNavHref(href?: string): boolean {
  if (!href) return false
  // Exact match or a sub-path. The trailing slash also keeps Home ('/') from
  // matching every path (which all start with '/').
  return DEMO_ALLOWED_NAV_HREFS.some(
    (allowed) => href === allowed || href.startsWith(`${allowed}/`)
  )
}

/**
 * True when a route path is reachable by a demo HR admin. Same as the nav
 * allow-list plus /profile, which is hidden from the nav but stays reachable.
 */
export function isDemoAllowedRoutePath(pathname?: string): boolean {
  if (!pathname) return false
  return isDemoAllowedNavHref(pathname) || pathname.startsWith('/profile')
}
